import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { NumberedPagination } from './interfaces';
import { RulerFactoryOption } from './enums';
import {
  MIN_NUMBER_IN_SECTION,
  START_PAGE,
  MIN_PAGE_LENGTH,
} from './constants';

@Component({
  selector: 'numbered-pagination',
  templateUrl: './numbered-pagination.component.html',
  styleUrls: ['./numbered-pagination.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberedPaginationComponent implements OnInit, OnChanges {
  @Input() pageLength: number = MIN_PAGE_LENGTH;
  @Input() value: number = START_PAGE;
  @Input() pageNumberInSection: number = MIN_NUMBER_IN_SECTION;
  @Input() isGoToFirstPageEnabled: boolean = false;
  @Input() isGoToLastPageEnabled: boolean = false;

  @Output() pageChanged: EventEmitter<number> = new EventEmitter<number>();

  public partStatus: boolean;
  public lastSection: boolean;
  public currentValue = START_PAGE;
  public partArray: number[] = [];
  public firstSection = true;

  constructor() { }

  public ngOnInit(): void {
    this.changePaginationState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.changePaginationState();
    if (changes.value) {
      this.navigateToPage(Number(this.value))
    }
    if (changes.pageNumberInSection) {
      this.pageNumberInSection = Number(this.pageNumberInSection);
    }
  }

  get pagination(): NumberedPagination {
    const { value, pageLength, pageNumberInSection } = this;
    const pages = ruler(value, pageLength, pageNumberInSection);
    return { value, pageLength, pages } as NumberedPagination;
  }

  navigateToPage(pageNumber: number): void {
    if (allowNavigation(pageNumber, this.value, this.pageLength)) {
      this.findCurrentSection(pageNumber);
      pageNumber = this.validatePageNumber(pageNumber);
      this.value = pageNumber;
      this.pageChanged.emit(this.value);
    }
  }

  trackByFn(value: number): number {
    return value;
  }

  private findCurrentSection(selectedPage: number, change?: boolean): void {
    selectedPage = this.validatePageNumber(selectedPage);
    const currentArr = [];
    const sectionMaxValue = Math.ceil(selectedPage / this.pageNumberInSection) * this.pageNumberInSection;
    [this.firstSection, this.lastSection] = [false, false];
    for (let i = sectionMaxValue - this.pageNumberInSection + 1; i < sectionMaxValue + 1; i++) {
      if (i <= this.pageLength) {
        currentArr.push(i);
      }
      if (sectionMaxValue >= this.pageLength) {
        this.lastSection = true;
      } else if (i <= 1) {
        this.firstSection = true;
      }
    }
    this.partArray = currentArr;
    if (!change) {
      this.value = Number(selectedPage);
    }
  }

  private validatePageNumber(value: number): number {
    if (value < MIN_PAGE_LENGTH) {
      value = MIN_PAGE_LENGTH;
    }
    if (value > this.pageLength) {
      value = this.pageLength;
    }
    return value;
  }

  private validateNumbeInSection(numberInSection: number, pageLength: number): number {
    const calcMaxnumberInSection = this.pageLength - MIN_NUMBER_IN_SECTION;
    const checkMinnumberInSection = this.pageLength > MIN_NUMBER_IN_SECTION * MIN_NUMBER_IN_SECTION;
    let validnumberInSection = numberInSection;
    this.partStatus = checkMinnumberInSection;
    if (numberInSection < MIN_NUMBER_IN_SECTION) {
      validnumberInSection = MIN_NUMBER_IN_SECTION;
    }
    if (numberInSection > Number(pageLength)) {
      validnumberInSection = pageLength;
    }
    if (numberInSection > calcMaxnumberInSection) {
      validnumberInSection = calcMaxnumberInSection;
    }
    if (!checkMinnumberInSection) {
      validnumberInSection = this.pageLength;
    }
    return validnumberInSection;
  }

  private changePaginationState(): void {
    if (!this.pageLength) {
      this.pageLength = MIN_PAGE_LENGTH;
    } else {
      this.pageLength = Number(this.pageLength);
    }
    this.pageNumberInSection = this.validateNumbeInSection(this.pageNumberInSection, this.pageLength);
    // generate page section
    this.partArray = Array.from({ length: this.pageNumberInSection }, (v, k) => k + 1);
    this.findCurrentSection(+this.value);
  }
}

const ruler = (
  currentvalue: number,
  maxPages: number,
  pageNumberInSection: number
): number[] => {
  const array = new Array(pageNumberInSection).fill(null);
  const min = Math.floor(pageNumberInSection / 2);
  return array.map((_, value) =>
    rulerFactory(currentvalue, value, min, maxPages, pageNumberInSection)
  );
};

const rulerOption = (
  currentvalue: number,
  min: number,
  maxPages: number
): RulerFactoryOption => {
  return currentvalue <= min
    ? RulerFactoryOption.Start
    : currentvalue >= maxPages - min
      ? RulerFactoryOption.End
      : RulerFactoryOption.Default;
};

const rulerFactory = (
  currentvalue: number,
  value: number,
  min: number,
  maxPages: number,
  pageNumberInSection: number
): number => {
  const factory = {
    [RulerFactoryOption.Start]: () => value + 1,
    [RulerFactoryOption.End]: () => maxPages - pageNumberInSection + value + 1,
    [RulerFactoryOption.Default]: () => currentvalue + value - min,
  };
  return factory[rulerOption(currentvalue, min, maxPages)]();
};

const allowNavigation = (
  pageNumber: number,
  value: number,
  maxPages: number
): boolean => {
  return pageNumber !== value && pageNumber > 0 && pageNumber <= maxPages;
};
